import { Component, computed, DestroyRef, ElementRef, HostListener, inject, LOCALE_ID, signal, ViewChild } from "@angular/core";
import { ButtonComponent } from "@app/kernel/shared/modules/button/components/button/button.component";
import { SvgIconComponent } from "@app/kernel/tools/svg-icon/components/svg-icon/svg-icon.component";
import { TranslatePipe } from "@ngx-translate/core";
import { SelectPreferredLangComponent } from "../select-preferred-lang/select-preferred-lang.component";
import { TranslationService } from "@app/kernel/translations/translation.service";
import { ELocalizationLang } from "@app/shared-core/enums/localization-lang.enum";
import { filter, finalize } from "rxjs";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AuthService } from "@app/kernel/tools/services/auth.service";
import { TopNavAbstraction } from "@app/shared-core/abstractions/top-nav.abstraction";
import { ResourcePipe } from "@app/kernel/tools/download-files/downloaded-files.config";
import { RouterLink } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { FeedbackModalIntegration } from "@sentry/core";
import { environment } from "@environments/environment";
import { openSentryFeedback } from "@app/shared-core/Initializers/sentry-feedback.Initializer";
@Component({
  selector: "app-top-nav",
  imports: [ResourcePipe, SvgIconComponent, TranslatePipe, ButtonComponent, SelectPreferredLangComponent, RouterLink],
  templateUrl: "./top-nav.component.html",
  styleUrl: "./top-nav.component.scss",
})
export class TopNavComponent {
  lang = inject(LOCALE_ID);
  destroyRef = inject(DestroyRef);
  authService = inject(AuthService);
  topNavAbstraction = inject(TopNavAbstraction);
  changeLangInProgress = false;

  showSentryReportButton = environment.SHOW_SENTRY_REPORT_BUTTON;

  private translationService = inject(TranslationService);

  showMenu = signal(false);

  @ViewChild("userMenu") userMenu!: ElementRef;
  @ViewChild("userMenuIcon") userMenuIcon!: ElementRef;

  langs: { name: string; code: ELocalizationLang }[] = [
    { name: "English", code: ELocalizationLang.EN },
    { name: "Italian", code: ELocalizationLang.IT },
  ];
  selectedLangCode = signal<{ name: string; code: ELocalizationLang } | undefined>(
    this.langs.find(lang => lang.code === this.translationService.getUserLang()) ?? this.langs[0]
  );
  isSessionSwitched = computed(() => this.topNavAbstraction.isSessionSwitched());
  originalUser = computed(() => this.topNavAbstraction.originalUser());

  user = toSignal(this.authService.user$);
  getInitials = computed(() => {
    const _name = this.user()?.name;
    if (!_name) return "";
    return _name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  });

  toggleLang() {}

  toggleMenu() {
    this.showMenu.update(value => !value);
  }

  logout() {
    this.authService.logout().subscribe();
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: Event) {
    const clickedInsideMenu = this.userMenu?.nativeElement.contains(event.target);
    const clickedToggleButton = this.userMenuIcon?.nativeElement.contains(event.target);
    if (!clickedInsideMenu && !clickedToggleButton && this.showMenu) {
      this.showMenu.set(false);
    }
  }

  undoSwitch() {
    this.topNavAbstraction.undoMySessionSwitch().subscribe();
  }

  async changeMyPreferredLang(lang: { name: string; code: ELocalizationLang } | undefined) {
    if (!lang) return;

    this.changeLangInProgress = true;
    this.topNavAbstraction
      .changeMyPreferredLang(lang.code)
      .pipe(
        filter(changed => changed),
        finalize(() => (this.changeLangInProgress = false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.selectedLangCode.set(lang);
      });
  }

  sentryFeedbackFormRef: ReturnType<FeedbackModalIntegration["createDialog"]> | undefined;

  async openFeedback(): Promise<void> {
    openSentryFeedback();
  }
}
